// HST — Vue aérienne 3D évolutive (remplace l'image « Vue aérienne — Site HST »)
// Dépendance : three@0.160.0
//
// <DepotVue3D depot={depot} tree={DEV_TREE} onBuild={(id) => construire(id)} onSelect={(sel) => …} />
//   depot.built     : identifiants construits de l'arbre (P2…P14, EXT)
//   depot.vehicles  : flotte du dépôt { id, plate, brand, model, type, body, driver, status, contract }
//   depot.contracts : contrats actifs { id, client, goods, volume, route }
//   depot.staff     : effectifs { chauffeurs, mecaniciens, caristes, exploitants, agents }
//   depot.cash      : trésorerie (active ou non le bouton Construire)
// 21/10/2026 — libellés d'interface bilingues (clés d3_* du système i18n du
// jeu). Les panneaux dessinés dans la scène restent dans la langue du pays du
// dépôt (depotProfiles.LANGS) ; seul le badge « zone disponible » suit l'UI.

import React, { useEffect, useRef, useState } from 'react';
import { createDepotScene } from './depotEngine';
import { DEV_TREE, GOODS, goodsLabel, bodyLabel, typeLabel, statusLabel, depotStats, coherence, fmtEur, nodeName, tierLabel } from './hstModel';
import Icon from './Icons';
import { profileOf } from './depotProfiles';
import { useTranslation } from '../../i18n';
import './DepotVue3D.css';

const fmt = (n) => new Intl.NumberFormat('fr-FR').format(n);
const TOPIC_KEYS = { rh: 'd3_topic_rh', flotte: 'd3_topic_flotte', contrat: 'd3_topic_contrat' };
const ROLE_KEYS = [['chauffeurs', 'd3_role_chauffeurs'], ['mecaniciens', 'd3_role_mecaniciens'], ['caristes', 'd3_role_caristes'], ['exploitants', 'd3_role_exploitants'], ['agents', 'd3_role_agents']];

function ZoneCard({ node, depot, tree, onBuild, onClose, t }) {
  const built = new Set(depot.built || []);
  const isBuilt = built.has(node.id);
  const missing = (node.requires || []).filter((r) => !built.has(r));
  const available = !isBuilt && (Array.isArray(depot.available) ? depot.available.includes(node.id) : missing.length === 0);
  const canPay = (depot.cash ?? Infinity) >= node.cost;
  const related = (depot.contracts || []).filter((c) => GOODS[c.goods]?.zone === node.id);
  return (
    <>
      <header className="hst3d-card__head">
        <span className={`hst3d-ico ${isBuilt ? 'is-ok' : available ? 'is-gold' : ''}`}><Icon name={node.icon} size={20} /></span>
        <div>
          <h3 className={available ? 'is-gold' : ''}>{node.name}</h3>
          <small>{node.id} · {tierLabel(node.tier)}</small>
        </div>
        <button className="hst3d-x" onClick={onClose} aria-label={t('d3_close')}><Icon name="x" /></button>
      </header>
      <p className="hst3d-effect"><span>{t('d3_effect')}</span> {node.effect}</p>
      <div className="hst3d-grid2">
        <div className="hst3d-box"><small>{t('d3_purchase')}</small><b>{fmtEur(node.cost)}</b></div>
        <div className="hst3d-box"><small>{t('d3_monthly_charge')}</small><b>{node.monthly ? `${fmt(node.monthly)} €` : '—'}</b></div>
      </div>
      {(node.condition || missing.length > 0) && (
        <div className="hst3d-box">
          <small>{t('d3_condition')}</small>
          {node.condition && <div>{node.condition}</div>}
          {missing.map((m) => <div key={m} className="hst3d-miss"><Icon name="lock" size={13} /> {m} · {nodeName(m, tree)}</div>)}
        </div>
      )}
      {related.length > 0 && (
        <div className="hst3d-box">
          <small>{t('d3_related_contracts')}</small>
          {related.map((c) => <div key={c.id}>{c.client} · {goodsLabel(c.goods)}</div>)}
        </div>
      )}
      {isBuilt ? (
        <div className="hst3d-done"><Icon name="check" /> {t('d3_zone_operational')}</div>
      ) : (
        <button className="hst3d-build" disabled={!available || !canPay} onClick={() => onBuild && onBuild(node.id)}>
          <Icon name="spark" /> {available ? (canPay ? t('d3_build_for', { cost: fmtEur(node.cost) }) : t('d3_insufficient_cash')) : t('d3_locked')}
        </button>
      )}
    </>
  );
}

function VehicleCard({ v, depot, onClose, t }) {
  const contract = (depot.contracts || []).find((c) => c.id === v.contract);
  const body = v.body || (v.type === 'vul' ? 'fourgon' : 'tautliner');
  const serves = Object.entries(GOODS).filter(([, g]) => g.bodies.includes(body)).map(([k]) => goodsLabel(k));
  return (
    <>
      <header className="hst3d-card__head">
        <span className="hst3d-ico"><Icon name="truck" size={20} /></span>
        <div>
          <h3>{v.model}</h3>
          <small>{v.plate} · {typeLabel(v.type)} {bodyLabel(body)}</small>
        </div>
        <button className="hst3d-x" onClick={onClose} aria-label={t('d3_close')}><Icon name="x" /></button>
      </header>
      <div className="hst3d-rows">
        <div><span>{t('d3_status')}</span><b className={v.status === 'mission' ? 'is-ok' : v.status === 'atelier' ? 'is-warn' : ''}>{statusLabel(v.status)}</b></div>
        <div><span>{t('d3_driver')}</span><b className={v.driver ? '' : 'is-warn'}>{v.driver || t('d3_unassigned')}</b></div>
        <div><span>{t('d3_contract')}</span><b>{contract ? `${contract.client}` : '—'}</b></div>
        {contract?.route && <div><span>{t('d3_route')}</span><b>{contract.route}</b></div>}
        <div><span>{t('d3_compatible')}</span><b>{serves.join(', ') || '—'}</b></div>
      </div>
    </>
  );
}

function QGCard({ depot, onClose, onPick, t }) {
  const st = depot.staff || {};
  const built = new Set(depot.built || []);
  const needs = {
    chauffeurs: (depot.vehicles || []).length,
    mecaniciens: built.has('P11') ? 2 : 0,
    caristes: built.has('P3') ? 2 : 0,
    exploitants: 1,
  };
  return (
    <>
      <header className="hst3d-card__head">
        <span className="hst3d-ico"><Icon name="users" size={20} /></span>
        <div>
          <h3>{t('d3_hq_card_title', { name: depot.name })}</h3>
          <small>{t('d3_hq_card_sub')}</small>
        </div>
        <button className="hst3d-x" onClick={onClose} aria-label={t('d3_close')}><Icon name="x" /></button>
      </header>
      <div className="hst3d-rows">
        {ROLE_KEYS.map(([k, key]) => {
          const n = st[k] || 0, need = needs[k] || 0;
          return <div key={k}><span>{t(key)}</span><b className={n < need ? 'is-warn' : ''}>{n}{need ? ` ${t('d3_required', { n: need })}` : ''}</b></div>;
        })}
      </div>
      <div className="hst3d-box">
        <small>{t('d3_active_contracts')}</small>
        {(depot.contracts || []).map((c) => {
          const zone = GOODS[c.goods]?.zone;
          const ok = !zone || built.has(zone);
          return (
            <button key={c.id} className="hst3d-line" onClick={() => onPick({ type: 'contract', id: c.id })}>
              <span>{c.client}</span>
              <em className={ok ? 'is-ok' : 'is-warn'}>{goodsLabel(c.goods)}</em>
            </button>
          );
        })}
      </div>
    </>
  );
}

function ContractCard({ c, depot, tree, onClose, onPick, t }) {
  const g = GOODS[c.goods];
  const built = new Set(depot.built || []);
  const zoneOk = !g.zone || built.has(g.zone);
  const vehicles = (depot.vehicles || []).filter((v) => g.bodies.includes(v.body || (v.type === 'vul' ? 'fourgon' : 'tautliner')));
  return (
    <>
      <header className="hst3d-card__head">
        <span className="hst3d-ico"><Icon name="file" size={20} /></span>
        <div>
          <h3>{c.client}</h3>
          <small>{c.id} · {goodsLabel(c.goods)}</small>
        </div>
        <button className="hst3d-x" onClick={onClose} aria-label={t('d3_close')}><Icon name="x" /></button>
      </header>
      <div className="hst3d-rows">
        {c.route && <div><span>{t('d3_route')}</span><b>{c.route}</b></div>}
        <div><span>{t('d3_required_zone')}</span><b className={zoneOk ? 'is-ok' : 'is-warn'}>{g.zone ? `${nodeName(g.zone, tree)}${zoneOk ? '' : ` ${t('d3_to_build')}`}` : '—'}</b></div>
        <div><span>{t('d3_compatible_vehicles')}</span><b className={vehicles.length ? 'is-ok' : 'is-warn'}>{vehicles.length ? vehicles.map((v) => v.plate).join(', ') : t('d3_none')}</b></div>
      </div>
      {!zoneOk && g.zone && <button className="hst3d-build is-ghost" onClick={() => onPick({ type: 'zone', id: g.zone })}><Icon name="spark" /> {t('d3_see_zone_named', { name: nodeName(g.zone, tree) })}</button>}
    </>
  );
}

export default function DepotVue3D({ depot, tree = DEV_TREE, onBuild, onSelect, selection: controlledSel, className = '' }) {
  const { t, language } = useTranslation();
  const hostRef = useRef(null);
  const engineRef = useRef(null);
  const [sel, setSel] = useState(null);
  const [showAlerts, setShowAlerts] = useState(false);
  const stats = depotStats(depot, tree);
  const alerts = coherence(depot, tree);

  // création du moteur (attend les polices pour les textures)
  useEffect(() => {
    let engine = null, cancelled = false;
    const ready = document.fonts?.ready ?? Promise.resolve();
    Promise.race([ready, new Promise((r) => setTimeout(r, 1500))]).then(() => {
      if (cancelled) return;
      engine = createDepotScene(hostRef.current, {
        depot, tree,
        labels: { available: t('d3_available_badge') },
        onSelect: (s) => { setSel(s); onSelect && onSelect(s); },
      });
      engineRef.current = engine;
      if (typeof window !== 'undefined') window.__hstDepot = engine;
    });
    return () => { cancelled = true; engine?.dispose(); engineRef.current = null; };
    // un dépôt différent (ou une autre langue d'interface) = une nouvelle scène
  }, [depot.id, language]); // eslint-disable-line react-hooks/exhaustive-deps

  // évolutions de l'état de jeu → mise à jour animée de la scène
  useEffect(() => { engineRef.current?.update(depot, tree); }, [depot, tree]);

  // sélection pilotée de l'extérieur (ex. clic sur une carte de l'arbre)
  useEffect(() => {
    if (controlledSel === undefined) return;
    setSel(controlledSel);
    engineRef.current?.select(controlledSel);
  }, [controlledSel]);

  const pick = (s) => { setSel(s); engineRef.current?.select(s); onSelect && onSelect(s); };
  const close = () => pick(null);

  let card = null;
  if (sel?.type === 'zone') {
    const node = tree.find((n) => n.id === sel.id);
    if (node) card = <ZoneCard node={node} depot={depot} tree={tree} onBuild={onBuild} onClose={close} t={t} />;
  } else if (sel?.type === 'vehicle') {
    const v = (depot.vehicles || []).find((x) => x.id === sel.id);
    if (v) card = <VehicleCard v={v} depot={depot} onClose={close} t={t} />;
  } else if (sel?.type === 'qg') card = <QGCard depot={depot} onClose={close} onPick={pick} t={t} />;
  else if (sel?.type === 'contract') {
    const c = (depot.contracts || []).find((x) => x.id === sel.id);
    if (c) card = <ContractCard c={c} depot={depot} tree={tree} onClose={close} onPick={pick} t={t} />;
  }

  const complete = stats.builtCount === stats.total;
  const prof = profileOf(depot);
  return (
    <section className={`hst3d ${className}`}>
      <div className={`hst3d-banner ${alerts.length ? 'is-warn' : ''}`}>
        <Icon name={alerts.length ? 'warning' : 'spark'} />
        {alerts.length ? (
          <>
            <b>{t(alerts.length > 1 ? 'd3_attention_points_plural' : 'd3_attention_points', { n: alerts.length })}</b>
            <span>— {alerts[0].text}</span>
            <button onClick={() => setShowAlerts((s) => !s)}>{showAlerts ? t('d3_hide') : t('d3_show_all')}</button>
          </>
        ) : complete ? (
          <><b>{t('d3_depot_complete')}</b><span>{t('d3_depot_complete_sub')}</span></>
        ) : (
          <><b>{t('d3_depot_coherent')}</b><span>{t('d3_depot_coherent_sub')}</span></>
        )}
      </div>
      {showAlerts && alerts.length > 0 && (
        <ul className="hst3d-alerts">
          {alerts.map((a, i) => (
            <li key={i}>
              <em>{t(TOPIC_KEYS[a.topic] || 'd3_topic_contrat')}</em>
              <span>{a.text}</span>
              {a.zone && <button onClick={() => pick({ type: 'zone', id: a.zone })}>{t('d3_see_zone')}</button>}
              {a.topic === 'rh' && <button onClick={() => pick({ type: 'qg', id: 'QG' })}>{t('d3_see_hq')}</button>}
            </li>
          ))}
        </ul>
      )}

      <div className="hst3d-stage">
        <div ref={hostRef} className="hst3d-canvas" />
        <div className="hst3d-pill hst3d-pill--tl"><Icon name="spark" size={14} /> {t('d3_phase', { n: stats.phase, total: stats.phases })} <span>· {stats.last}</span></div>
        <div className="hst3d-pill hst3d-pill--tl2"><Icon name="globe" size={13} /> {t(prof.country.key)} <span>· {t(prof.theme.key)}{prof.rival ? ` ${t('d3_acquired_from', { name: prof.rival.name })}` : ''}</span></div>
        <button className="hst3d-pill hst3d-pill--tr" onClick={() => pick({ type: 'qg', id: 'QG' })} title={t('d3_fleet_staff_title')}>
          <Icon name="truck" size={14} /> {stats.parked} {t('d3_vehicles_short')} <span>{t('d3_slots', { n: stats.slots })}</span>
        </button>
        <div className="hst3d-pill hst3d-pill--bl"><Icon name="cursor" size={13} /> {t('d3_click_hint_pre')} <b>{t('d3_available_badge')}</b>{t('d3_click_hint_post')}</div>
        <div className="hst3d-tools" role="toolbar" aria-label={t('d3_camera')}>
          <button onClick={() => engineRef.current?.rotate(-1)} aria-label={t('d3_rotate_left')} title={t('d3_rotate_left')}><Icon name="rotL" /></button>
          <button onClick={() => engineRef.current?.rotate(1)} aria-label={t('d3_rotate_right')} title={t('d3_rotate_right')}><Icon name="rotR" /></button>
          <button onClick={() => engineRef.current?.zoom(0.8)} aria-label={t('d3_zoom_in')} title={t('d3_zoom_in')}><Icon name="plus" /></button>
          <button onClick={() => engineRef.current?.zoom(1.25)} aria-label={t('d3_zoom_out')} title={t('d3_zoom_out')}><Icon name="minus" /></button>
          <button onClick={() => { engineRef.current?.home(); setSel(null); }} aria-label={t('d3_overview')} title={t('d3_overview')}><Icon name="home" /></button>
        </div>
        {card && <aside className="hst3d-card" aria-live="polite">{card}</aside>}
      </div>
    </section>
  );
}
